import { Clock } from "@phosphor-icons/react"
import { Spinner } from "@vesatogo/grass-core"
import classNames from "classnames"
import dayjs from "dayjs"
import duration from "dayjs/plugin/duration"
import { useEffect, useState } from "react"
import { OperationContext } from "urql"

dayjs.extend(duration)

export const CountDownTimer = ({
  startTime,
  endTime,
  currentStatus,
  refetch,
}: {
  startTime: dayjs.Dayjs
  endTime: dayjs.Dayjs
  currentStatus: "created" | "uploading_enquiries" | "live" | "closed"
  refetch: (opts?: Partial<OperationContext> | undefined) => void
}) => {
  // Example start & end times

  const [time, setTime] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  })

  const [status, setStatus] = useState<
    "uploading" | "pending" | "running" | "ended"
  >("pending")

  useEffect(() => {
    const timer = setInterval(() => {
      if (currentStatus === "closed") {
        setStatus("ended")
        setTime({ hours: 0, minutes: 0, seconds: 0 })
        clearInterval(timer)
        return
      }
      const now = dayjs()

      // BEFORE START
      if (currentStatus === "created") {
        setStatus("pending")
        return
      }

      if (currentStatus === "uploading_enquiries") {
        setStatus("uploading")
        return
      }

      // RUNNING
      setStatus("running")
      const diff = endTime.diff(now)
      const d = dayjs.duration(diff)

      setTime({
        hours: d.hours(),
        minutes: d.minutes(),
        seconds: d.seconds(),
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [startTime, endTime])

  const pad = (num: number) => String(num).padStart(2, "0")

  return (
    <div
      className={classNames(
        " p-4 rounded-lg flex flex-col gap-2",
        currentStatus === "closed"
          ? "!bg-[#FFDDDD]"
          : "bg-gradient-to-r from-[#00BC7D] to-[#009689]"
      )}
    >
      <p
        className={classNames(
          "text-lg text-white flex gap-1 items-center font-500",
          currentStatus === "closed" && "!text-[#999999]"
        )}
      >
        <Clock size={24} />
        Live Countdown Timer
      </p>

      {(status === "running" || status === "ended") && (
        <p
          className={classNames(
            "text-center text-white font-600 text-2xl",
            currentStatus === "closed" && "!text-[#999999]"
          )}
        >
          {pad(time.hours)} <span className="text-xs font-400">hrs</span> :{" "}
          {pad(time.minutes)} <span className="text-xs font-400">min</span> :{" "}
          {pad(time.seconds)} <span className="text-xs font-400">sec</span>
        </p>
      )}

      {status === "pending" ||
        (status === "uploading" && (
          <p
            className={classNames(
              "bg-white text-[#009966] p-1 rounded-md w-fit text-sm mx-auto font-500 opacity-90"
            )}
          >
            Starts at {startTime.format("DD MMM 'YY | hh:mm A")}
          </p>
        ))}

      <p
        className={classNames(
          "bg-white text-[#009966] p-1 rounded-md w-fit text-sm mx-auto font-500 opacity-90",
          status === "ended" && "!text-green-500",
          (status === "pending" ||
            status === "running" ||
            status === "uploading") &&
            "!text-red"
        )}
      >
        Ends at {endTime.format("DD MMM 'YY | hh:mm A")}{" "}
        <span className="text-red-500 font-600">
          {currentStatus === "closed" && " | CLOSED"}
        </span>
      </p>

      {status === "uploading" && (
        <p
          className={classNames(
            "bg-white text-[#009966] p-1 rounded-md w-fit text-sm mx-auto font-700 opacity-90 flex gap-2 "
          )}
        >
          Uploading Users <Spinner size={"sm"} />
        </p>
      )}
    </div>
  )
}
